/**
 * Firebase REST API helpers - COMPLETE REPLACEMENT FOR SDK
 * Web-only implementation using HTTP directly
 * Mobile still uses Firebase SDK
 */

const PROJECT_ID = 'pyxida-e6211';
const API_KEY = 'AIzaSyA-ZoLrIZy10-lsoxo4m3SL9zRI1oTaoTI';
const DATABASE = '(default)';
const BASE_URL = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/${DATABASE}/documents`;

/**
 * ============================================================================
 * WRITE OPERATIONS
 * ============================================================================
 */

/**
 * Write a document directly via Firebase REST API
 * Equivalent to: addDoc(collection, data)
 */
export async function writeDocumentViaREST(
  collectionName: string,
  documentData: any
): Promise<{ name: string; id: string }> {
  try {
    console.log(`📡 [REST-API] Writing to ${collectionName} via HTTP...`);
    
    const url = `${BASE_URL}/${collectionName}`;
    
    // Convert data to Firestore format
    const firestoreData: any = {};
    for (const [key, value] of Object.entries(documentData)) {
      firestoreData[key] = convertToFirestoreValue(value);
    }
    
    console.log(`📡 [REST-API] POST ${collectionName}`);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fields: firestoreData,
      }),
    });
    
    if (!response.ok) {
      const error = await response.text();
      console.error(`❌ [REST-API] HTTP ${response.status}: ${error}`);
      throw new Error(`HTTP ${response.status}: ${error}`);
    }
    
    const result = await response.json();
    console.log(`✅ [REST-API] Document written: ${result.name}`);
    
    const docId = result.name.split('/').pop();
    return { name: result.name, id: docId };
  } catch (error) {
    console.error(`❌ [REST-API] Error writing document:`, error);
    throw error;
  }
}

/**
 * Update a document via Firebase REST API
 * Equivalent to: updateDoc(doc, data)
 * CRITICAL: Must include updateMask query parameter to do a partial update, not a full replacement
 */
export async function updateDocumentViaREST(
  collectionName: string,
  documentId: string,
  updateData: any
): Promise<void> {
  try {
    console.log(`📡 [REST-API] Updating ${collectionName}/${documentId} via HTTP...`);
    console.log(`📡 [REST-API] Update fields:`, Object.keys(updateData));
    
    const firestoreData: any = {};
    const updateMaskPaths: string[] = [];
    
    for (const [key, value] of Object.entries(updateData)) {
      firestoreData[key] = convertToFirestoreValue(value);
      updateMaskPaths.push(key);
    }
    
    // Build URL with updateMask as query parameter
    const updateMaskQuery = updateMaskPaths.map(path => `updateMask.fieldPaths=${encodeURIComponent(path)}`).join('&');
    const url = `${BASE_URL}/${collectionName}/${documentId}?${updateMaskQuery}`;
    
    console.log(`📡 [REST-API] PATCH ${collectionName}/${documentId} with updateMask:`, updateMaskPaths);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fields: firestoreData,
      }),
    });
    
    if (!response.ok) {
      const error = await response.text();
      console.error(`❌ [REST-API] HTTP ${response.status}: ${error}`);
      throw new Error(`HTTP ${response.status}: ${error}`);
    }
    
    console.log(`✅ [REST-API] Document updated: ${collectionName}/${documentId}`);
  } catch (error) {
    console.error(`❌ [REST-API] Error updating document:`, error);
    throw error;
  }
}

/**
 * Delete a document via Firebase REST API
 * Equivalent to: deleteDoc(doc)
 */
export async function deleteDocumentViaREST(
  collectionName: string,
  documentId: string
): Promise<void> {
  try {
    console.log(`� [REST-API] Deleting ${collectionName}/${documentId} via HTTP...`);
    
    const url = `${BASE_URL}/${collectionName}/${documentId}`;
    
    console.log(`📡 [REST-API] DELETE ${collectionName}/${documentId}`);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok && response.status !== 404) {
      const error = await response.text();
      console.error(`❌ [REST-API] HTTP ${response.status}: ${error}`);
      throw new Error(`HTTP ${response.status}: ${error}`);
    }
    
    console.log(`✅ [REST-API] Document deleted: ${collectionName}/${documentId}`);
  } catch (error) {
    console.error(`❌ [REST-API] Error deleting document:`, error);
    throw error;
  }
}

/**
 * ============================================================================
 * READ OPERATIONS
 * ============================================================================
 */

/**
 * Read a single document via Firebase REST API
 * Equivalent to: getDoc(doc)
 */
export async function readDocumentViaREST(
  collectionName: string,
  documentId: string
): Promise<any | null> {
  try {
    console.log(`📖 [REST-API] Reading ${collectionName}/${documentId} via HTTP...`);
    
    const url = `${BASE_URL}/${collectionName}/${documentId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (response.status === 404) {
      console.log(`⚠️ [REST-API] Document not found: ${collectionName}/${documentId}`);
      return null;
    }
    
    if (!response.ok) {
      const error = await response.text();
      console.error(`❌ [REST-API] HTTP ${response.status}: ${error}`);
      throw new Error(`HTTP ${response.status}: ${error}`);
    }
    
    const result = await response.json();
    console.log(`✅ [REST-API] Document read: ${result.name}`);
    
    if (result.fields) {
      const data: any = {};
      for (const [fieldName, fieldValue] of Object.entries(result.fields)) {
        data[fieldName] = convertFromFirestoreValue(fieldValue);
      }
      return { ...data, id: documentId };
    }
    
    return null;
  } catch (error) {
    console.error(`❌ [REST-API] Error reading document:`, error);
    throw error;
  }
}

/**
 * Read all documents from a collection with optional WHERE filters
 * Equivalent to: getDocs(query(collection, where(...), where(...)))
 * 
 * Example:
 * readCollectionViaREST('chats', [
 *   { field: 'userIds', operator: 'array-contains', value: userId },
 *   { field: 'status', operator: '==', value: 'active' }
 * ])
 */
export async function readCollectionViaREST(
  collectionName: string,
  filters?: Array<{ field: string; operator: string; value: any }>
): Promise<Array<{ id: string; [key: string]: any }>> {
  try {
    console.log(`📚 [REST-API] Reading ${collectionName} with ${filters?.length || 0} filters...`);
    
    const url = `${BASE_URL}/${collectionName}`;
    
    // Use runQuery for complex queries with filters
    if (filters && filters.length > 0) {
      return await queryCollectionViaREST(collectionName, filters);
    }
    
    // Simple list all documents
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const error = await response.text();
      console.error(`❌ [REST-API] HTTP ${response.status}: ${error}`);
      throw new Error(`HTTP ${response.status}: ${error}`);
    }
    
    const result = await response.json();
    console.log(`✅ [REST-API] Read collection: ${collectionName}, documents: ${result.documents?.length || 0}`);
    
    return (result.documents || []).map((doc: any) => {
      const id = doc.name.split('/').pop();
      const data = convertFromFirestoreValue(doc.fields || {});
      return { id, ...data };
    });
  } catch (error) {
    console.error(`❌ [REST-API] Error reading collection:`, error);
    throw error;
  }
}

/**
 * Query collection with WHERE filters
 * Uses the runQuery endpoint for complex queries
 */
async function queryCollectionViaREST(
  collectionName: string,
  filters: Array<{ field: string; operator: string; value: any }>
): Promise<Array<{ id: string; [key: string]: any }>> {
  try {
    console.log(`🔍 [REST-API] Querying ${collectionName} with filters:`, filters);
    
    const url = `${BASE_URL}:runQuery`;
    
    // Build WHERE clause
    const where = {
      compositeFilter: {
        op: 'AND',
        filters: filters.map(f => ({
          fieldFilter: {
            field: { fieldPath: f.field },
            op: convertOperator(f.operator),
            value: convertToFirestoreValue(f.value),
          },
        })),
      },
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        structuredQuery: {
          from: [{ collectionId: collectionName }],
          where,
        },
      }),
    });
    
    if (!response.ok) {
      const error = await response.text();
      console.error(`❌ [REST-API] HTTP ${response.status}: ${error}`);
      throw new Error(`HTTP ${response.status}: ${error}`);
    }
    
    const result = await response.json();
    console.log(`✅ [REST-API] Query results: ${result.length || 0} documents`);
    
    return (result || [])
      .filter((item: any) => item.document)
      .map((item: any) => {
        const doc = item.document;
        const id = doc.name.split('/').pop();
        // Convert each field in the document
        const data: any = {};
        if (doc.fields) {
          for (const [fieldName, fieldValue] of Object.entries(doc.fields)) {
            data[fieldName] = convertFromFirestoreValue(fieldValue);
          }
        }
        return { id, ...data };
      });
  } catch (error) {
    console.error(`❌ [REST-API] Error querying collection:`, error);
    throw error;
  }
}

/**
 * ============================================================================
 * REAL-TIME LISTENERS (Polling Implementation)
 * ============================================================================
 */

const activeListeners = new Map<string, NodeJS.Timeout>();

/**
 * Set up a polling-based listener for real-time updates
 * Equivalent to: onSnapshot(query, callback)
 * 
 * Polls the backend every 'interval' milliseconds and calls the callback
 * when data changes
 */
export function listenToCollectionViaREST(
  collectionName: string,
  filters: Array<{ field: string; operator: string; value: any }> | undefined,
  onUpdate: (docs: Array<{ id: string; [key: string]: any }>) => void,
  onError?: (error: any) => void,
  interval: number = 2000  // Poll every 2 seconds
): () => void {
  console.log(`👂 [REST-API] Setting up listener for ${collectionName} (polling every ${interval}ms)...`);
  
  let lastSnapshot: string = '';
  let isFirstCall = true;
  
  const listenerId = `${collectionName}:${filters?.map(f => `${f.field}=${f.value}`).join(',') || 'all'}`;
  
  // Clear any existing listener
  if (activeListeners.has(listenerId)) {
    clearInterval(activeListeners.get(listenerId)!);
  }
  
  // Immediately fetch and return current data
  readCollectionViaREST(collectionName, filters)
    .then(docs => {
      const snapshot = JSON.stringify(docs);
      lastSnapshot = snapshot;
      onUpdate(docs);
    })
    .catch(err => {
      console.error(`❌ [REST-API] Error in listener initial fetch:`, err);
      onError?.(err);
    });
  
  // Set up polling
  const intervalId = setInterval(async () => {
    try {
      const docs = await readCollectionViaREST(collectionName, filters);
      const snapshot = JSON.stringify(docs);
      
      // Only call callback if data changed
      if (snapshot !== lastSnapshot) {
        console.log(`📡 [REST-API] Listener detected change for ${collectionName}`);
        lastSnapshot = snapshot;
        onUpdate(docs);
      }
    } catch (error) {
      console.error(`❌ [REST-API] Error in listener poll:`, error);
      onError?.(error);
    }
  }, interval);
  
  activeListeners.set(listenerId, intervalId);
  
  // Return unsubscribe function
  return () => {
    console.log(`👋 [REST-API] Unsubscribing from listener: ${listenerId}`);
    const timer = activeListeners.get(listenerId);
    if (timer) {
      clearInterval(timer);
      activeListeners.delete(listenerId);
    }
  };
}

/**
 * ============================================================================
 * TYPE CONVERTERS
 * ============================================================================
 */

/**
 * Convert Firestore REST API format back to JavaScript values
 */
function convertFromFirestoreValue(value: any): any {
  if (value === null || value === undefined) {
    return null;
  }
  
  if (value.nullValue !== undefined) {
    return null;
  }
  
  if (value.stringValue !== undefined) {
    return value.stringValue;
  }
  
  if (value.integerValue !== undefined) {
    return parseInt(value.integerValue, 10);
  }
  
  if (value.doubleValue !== undefined) {
    return value.doubleValue;
  }
  
  if (value.booleanValue !== undefined) {
    return value.booleanValue;
  }
  
  if (value.timestampValue !== undefined) {
    return new Date(value.timestampValue);
  }
  
  // CRITICAL: Array handling - must come before map handling
  if (value.arrayValue !== undefined) {
    if (!value.arrayValue || !value.arrayValue.values) {
      return [];
    }
    if (!Array.isArray(value.arrayValue.values)) {
      console.warn('[REST-API] arrayValue.values is not an array:', value.arrayValue.values);
      return [];
    }
    return value.arrayValue.values.map((v: any) => convertFromFirestoreValue(v));
  }
  
  if (value.mapValue && value.mapValue.fields) {
    const result: any = {};
    for (const [k, v] of Object.entries(value.mapValue.fields)) {
      result[k] = convertFromFirestoreValue(v);
    }
    return result;
  }
  
  console.warn(`[REST-API] Unknown type for value:`, value);
  return value;
}

/**
 * Convert JavaScript values to Firestore REST API format
 */
export function convertToFirestoreValue(value: any): any {
  if (value === null) {
    return { nullValue: null };
  }
  
  if (typeof value === 'string') {
    return { stringValue: value };
  }
  
  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return { integerValue: value.toString() };
    } else {
      return { doubleValue: value };
    }
  }
  
  if (typeof value === 'boolean') {
    return { booleanValue: value };
  }
  
  if (value instanceof Date) {
    return { timestampValue: value.toISOString() };
  }
  
  if (Array.isArray(value)) {
    return {
      arrayValue: {
        values: value.map(v => convertToFirestoreValue(v)),
      },
    };
  }
  
  if (typeof value === 'object') {
    const fields: any = {};
    for (const [k, v] of Object.entries(value)) {
      fields[k] = convertToFirestoreValue(v);
    }
    return { mapValue: { fields } };
  }
  
  console.warn(`[REST-API] Unknown type for value:`, value);
  return { stringValue: String(value) };
}

/**
 * Convert SDK operator names to REST API operators
 */
function convertOperator(op: string): string {
  const operatorMap: { [key: string]: string } = {
    '==': 'EQUAL',
    '<': 'LESS_THAN',
    '<=': 'LESS_THAN_OR_EQUAL',
    '>': 'GREATER_THAN',
    '>=': 'GREATER_THAN_OR_EQUAL',
    '!=': 'NOT_EQUAL',
    'array-contains': 'ARRAY_CONTAINS',
    'in': 'IN',
    'array-contains-any': 'ARRAY_CONTAINS_ANY',
  };
  return operatorMap[op] || op;
}
